<script setup lang="ts">
import type {
  DailyCheckinPopupTheme,
  DailyCheckinRewardView,
} from '~/types/daily-checkin';
import { getAssetsUrlForCDN } from '@/utils/template';

// Popup do degrau — frame "Resgate" do Figma (41:2033), 250px de largura:
//   Resgatar (41:2032)          → azul, manchete + arte 160 + botão RESGATAR
//   Prop3 (198:4874)            → navy, sem botão (degrau ainda não alcançado)
//   Resgatado State 01 (41:2034)→ verde, arte + check + botão USAR
//
// A variante Prop4 (arte menor + nome + descrição no corpo) foi descartada: a
// ficha do prêmio virou o popover do (i), igual ao da tela do calendário.
//
// Vive num overlay próprio (z-9997) em vez de dentro do painel do calendário:
// o painel tem `overflow: clip` e recortaria o popup.
const props = defineProps<{
  reward: DailyCheckinRewardView | null;
  // Resgate em voo: trava o botão.
  claiming?: boolean;
  // Mensagem de recusa já resolvida pela store (por código de erro).
  error?: string | null;
  // Cores e textos vindos do backoffice. Sempre presente: a store cai no visual
  // de hoje enquanto o board não chegou, então aqui não há estado sem tema.
  theme: DailyCheckinPopupTheme;
  // Esconder O QUE é o prêmio. Precisa valer AQUI e não só na trilha: a célula
  // escondida continua clicável, e sem isto um toque revelaria no popup
  // exatamente o que a opção existe para não mostrar.
  hidePrize?: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'claim'): void;
  // Destino JÁ resolvido (contrato ou fallback). Quem navega é o modal: ele é
  // que sabe fechar o painel antes de trocar de rota.
  (e: 'use', link: string): void;
}>();

// Destino do USAR quando o contrato não fala do assunto: a tela de recompensas
// da conta. Todo prêmio da trilha é creditado como recompensa, então ela é o
// lugar onde qualquer um deles pode ser visto — é o fallback que nunca aponta
// pro prêmio errado.
const REWARDS_FALLBACK = '/user/rewards';

// `visible` mantém o overlay montado até a animação de saída terminar.
const visible = ref(false);
const shown = ref(false);

// Ficha do prêmio (nome + descrição) num popover ancorado no (i), com a mesma
// barriguinha e o mesmo clique-fora do popover da tela do calendário.
//
// Diverge da variante Prop4 do Figma, que trocava o corpo do card (arte menor
// + nome + descrição no lugar da manchete): decisão do Lucas de padronizar as
// duas telas no mesmo dropdown. Por isso a ficha some do corpo e o card fica
// sempre com manchete + arte grande, em todos os tons.
const infoOpen = ref(false);

const isClaimed = computed(() => props.reward?.state === 'claimed');
const isClaimable = computed(() => props.reward?.state === 'claimable');

// Dois tons e o azul de todo o resto. Existiu um terceiro, vermelho, pro degrau
// fora de alcance — saiu junto com o vermelho da célula: o popup nunca contradiz
// a cor da trilha que o abriu.
const tone = computed(() => {
  if (isClaimed.value) return 'claimed';
  if (isClaimable.value) return 'claimable';

  return 'locked';
});

// O tema entra como CUSTOM PROPERTIES no card, não como estilo em cada
// elemento: as cores são usadas em lugares que não têm elemento próprio pra
// receber `:style` — o cross-fade da comemoração é um pseudo-elemento, e os
// gradientes têm paradas de porcentagem que devem continuar escritas no CSS.
// Aqui viajam só os valores; a composição continua na folha de estilo.
// Quanto de branco a ARESTA leva: o mesmo 22% do `color-mix` da folha de estilo
// (78% da base). Repetido aqui porque a escolha de tinta abaixo precisa saber o
// tom que vai APARECER, e `color-mix` só existe depois do CSS.
const EDGE_WHITE = 0.22;

/** `#RRGGBB` → [r, g, b]. Qualquer outra grafia cai no preto. */
function rgb(hex: string): [number, number, number] {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());

  if (!m) return [0, 0, 0];

  const [, r = '0', g = '0', b = '0'] = m;

  return [parseInt(r, 16), parseInt(g, 16), parseInt(b, 16)];
}

// Preto ou branco sobre `hex`, o que tiver mais contraste (YIQ, corte em 150).
// `white` é quanto a cor foi clareada antes de chegar na tela.
//
// Existe porque as cores são LIVRES no backoffice e o glifo dos ícones era
// branco fixo: num fundo dourado o X de fechar sumia, e o backdrop virava a
// única saída do popup. Vale o mesmo pra legenda do RESGATAR e pros textos da
// ficha, todos sobre cor configurável.
//
// Ela NÃO PINTA nada — quem pinta continua sendo o `color-mix` da folha, o
// mesmo da prévia do backoffice. Aqui só se escolhe entre duas tintas, e um
// ponto percentual de diferença só mudaria a escolha num tom que já está em
// cima da fronteira.
function readableOn(hex: string, white = 0): string {
  const [r, g, b] = rgb(hex);
  const lift = (c: number) => c + (255 - c) * white;

  return (lift(r) * 299 + lift(g) * 587 + lift(b) * 114) / 1000 >= 150
    ? '#000000'
    : '#FFFFFF';
}

const themeVars = computed(() => ({
  '--dcp-available-from': props.theme.availableBackgroundFrom,
  '--dcp-available-to': props.theme.availableBackgroundTo,
  '--dcp-claimed-from': props.theme.claimedBackgroundFrom,
  '--dcp-claimed-to': props.theme.claimedBackgroundTo,
  '--dcp-claim-btn': props.theme.buttonColor,
  '--dcp-desc': props.theme.descriptionColor,
  // Uma tinta por superfície de cor CONFIGURÁVEL, no mesmo vocabulário do
  // formulário: o TOPO (`from`, fundo da ficha), a BASE (`to`, fundo do selo),
  // a ARESTA (glifo dos ícones e botão da ficha) e a cor do botão de resgate.
  //
  // O que fica de fora é o que se apoia no GRADIENTE inteiro — a manchete, o
  // USAR, o "?" —, que já era branco antes desta feature: ali o piso seria
  // arbitrário, porque a cor de baixo muda ao longo do card.
  '--dcp-available-top-ink': readableOn(props.theme.availableBackgroundFrom),
  '--dcp-claimed-top-ink': readableOn(props.theme.claimedBackgroundFrom),
  '--dcp-available-base-ink': readableOn(props.theme.availableBackgroundTo),
  '--dcp-claimed-base-ink': readableOn(props.theme.claimedBackgroundTo),
  '--dcp-available-edge-ink': readableOn(
    props.theme.availableBackgroundTo,
    EDGE_WHITE,
  ),
  '--dcp-claimed-edge-ink': readableOn(
    props.theme.claimedBackgroundTo,
    EDGE_WHITE,
  ),
  '--dcp-claim-ink': readableOn(props.theme.buttonColor),
}));

const headline = computed(() =>
  props.hidePrize ? 'Prêmio ainda não revelado' : (props.reward?.title ?? ''),
);

const prizeImage = computed(() =>
  props.reward ? getAssetsUrlForCDN(props.reward.imageUrl) : '',
);

const asset = (file: string) =>
  getAssetsUrlForCDN(`/assets/images/daily-checkin/${file}`);

// Os ícones viraram SVG inline no template (ver o comentário lá): o glifo é
// idêntico nos dois tons — só o círculo mudava de cor —, e como `currentColor`
// ele acompanha o tema em vez de ficar preso ao azul ou ao verde do arquivo.

// Agora o (i) existe em todos os tons: com a ficha no dropdown, sem ele o
// navy não teria como mostrar a descrição.
// A ficha do (i) é a descrição do prêmio: com o prêmio escondido ela some
// junto, senão o botão continuaria contando o que a arte deixou de mostrar.
const hasInfo = computed(() => !props.hidePrize && !!props.reward?.description);

// Destino do botão USAR, resolvido pelo tri-estado do contrato (ver
// DailyCheckinReward.ctaUrl):
//
//   chave ausente → /user/rewards (o core ainda não manda destino)
//   chave vazia   → null, e sem destino não há botão
//   com valor     → o destino cadastrado
//
// `undefined` também é o que se lê com o popup fechado (reward null), e aí o
// valor não é consultado — nada renderiza.
// (`ctaLink` e não `useLink`: esse nome é de um composable do vue-router que o
// Nuxt auto-importa, e um `const` local por cima dele confunde mais do que
// economiza.)
const ctaLink = computed(() => {
  const raw = props.reward?.ctaUrl;

  if (raw === undefined) return REWARDS_FALLBACK;

  return raw || null;
});

// O USAR só existe no degrau resgatado: é o prêmio que já está na conta. Nos
// outros tons não há o que usar.
const hasUse = computed(() => isClaimed.value && !!ctaLink.value);

/* ---------------- comemoração do resgate ---------------- */

// Faíscas do estouro: ângulos distribuídos em volta do prêmio, com distância e
// atraso variando por índice. Valores fixos (e não Math.random) pra a animação
// ser sempre a mesma e não divergir entre renders.
const SPARKS = Array.from({ length: 16 }, (_, i) => ({
  angle: (360 / 16) * i + (i % 2 ? 11 : -7),
  distance: [104, 80, 122][i % 3],
  delay: (i % 5) * 26,
  size: i % 4 === 0 ? 13 : 9,
}));

// A comemoração — pulo do card, virada de cor, carimbo do check e as faíscas
// saindo do centro — só acontece quando o resgate cai com o popup aberto. É a
// PASSAGEM de um estado pro outro; reabrir um degrau já resgatado não repete.
//
// O leque de raios é a exceção: ele não marca a passagem, marca o prêmio, e
// por isso acompanha todo degrau resgatado.
const celebrating = ref(false);

let celebrationTimer: ReturnType<typeof setTimeout> | null = null;

function stopCelebration() {
  if (celebrationTimer) {
    clearTimeout(celebrationTimer);
    celebrationTimer = null;
  }

  celebrating.value = false;
}

watch(
  () => props.reward?.state,
  (state, previous) => {
    if (state !== 'claimed' || previous !== 'claimable') return;

    stopCelebration();
    celebrating.value = true;
    // Desmonta as camadas quando a animação mais longa termina, pra não deixar
    // pseudo-elementos animados pendurados no card.
    celebrationTimer = setTimeout(() => {
      celebrating.value = false;
      celebrationTimer = null;
    }, 1200);
  },
);

onBeforeUnmount(stopCelebration);

watch(
  () => props.reward,
  async (value) => {
    if (value) {
      infoOpen.value = false;
      visible.value = true;
      await nextTick();
      shown.value = true;
    } else {
      shown.value = false;
    }
  },
  { immediate: true },
);

function onClosed() {
  visible.value = false;
  infoOpen.value = false;
  stopCelebration();
}

function close() {
  emit('close');
}

function onClaim() {
  if (!props.reward || props.claiming) return;
  emit('claim');
}

function onUse() {
  if (!ctaLink.value) return;
  emit('use', ctaLink.value);
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="dcp-overlay"
      @dragstart.prevent
    >
      <div
        class="dcp-backdrop"
        :class="{ 'dcp-backdrop--out': !shown }"
        @click="close"
      />

      <Transition
        name="dcp-pop"
        @after-leave="onClosed"
      >
        <div
          v-if="shown && reward"
          class="dcp-card"
          :class="[`dcp-card--${tone}`, { 'dcp-card--won': celebrating }]"
          :style="themeVars"
        >
          <!-- SVG inline, e não mais os arquivos info.svg/close.svg: nenhuma
               variável CSS alcança o interior de um <img>, então os ícones
               ficavam azuis num card magenta. Inline, o círculo herda a cor do
               botão e o glifo leva a tinta do tom (o `fill` aqui é só o
               fallback; quem manda é o `path` da folha de estilo).
               O info.svg/close.svg da pasta PAI seguem em uso no calendário e
               em outras telas; as variantes `-green`, exclusivas deste card,
               saíram junto. -->
          <button
            v-if="hasInfo"
            aria-label="Sobre o prêmio"
            class="dcp-icon dcp-icon--info"
            type="button"
            @click="infoOpen = !infoOpen"
          >
            <svg
              aria-hidden="true"
              fill="none"
              viewBox="0 0 30 30"
            >
              <rect
                fill="currentColor"
                height="30"
                rx="15"
                width="30"
              />
              <path
                d="M16 7V9.66667H13.5V7H16ZM12 12.3333H13H15H16V13.4V20.8667H17H18V23H17H16H14H13H12V20.8667H13H14V14.4667H13H12V12.3333Z"
                fill="#fff"
              />
            </svg>
          </button>

          <button
            aria-label="Fechar"
            class="dcp-icon dcp-icon--close"
            type="button"
            @click="close"
          >
            <svg
              aria-hidden="true"
              fill="none"
              viewBox="0 0 30 30"
            >
              <rect
                fill="currentColor"
                height="30"
                rx="15"
                width="30"
              />
              <path
                d="M19.2784 12.1583L20 11.4399L18.5601 10L17.8417 10.7184L15 13.5601L12.1583 10.7184L11.4399 10L10 11.4399L10.7184 12.1583L13.5601 15L10.7216 17.8417L10 18.5601L11.4399 20L12.1583 19.2816L15 16.4399L17.8417 19.2816L18.5601 20L20 18.5601L19.2816 17.8417L16.4399 15L19.2816 12.1583H19.2784Z"
                fill="#fff"
              />
            </svg>
          </button>

          <!-- Scrim invisível: fecha o popover ao clicar fora, como na tela do
               calendário. -->
          <div
            v-if="infoOpen"
            class="dcp-info-scrim"
            @click="infoOpen = false"
          />

          <!-- Ficha do prêmio, ancorada no (i). -->
          <Transition name="dcp-fade">
            <div
              v-if="infoOpen"
              class="dcp-info"
            >
              <p
                v-if="reward.title"
                class="dcp-info__name"
                >{{ reward.title }}</p
              >
              <p class="dcp-info__text">{{ reward.description }}</p>
              <button
                class="dcp-info__ok"
                type="button"
                @click="infoOpen = false"
              >
                Entendi
              </button>
            </div>
          </Transition>

          <span class="dcp-badge">{{ reward.position }}º Dia</span>

          <p
            class="dcp-headline"
            :class="{ 'dcp-headline--won': celebrating }"
          >
            {{ headline }}
            <!-- Frase própria, sem concordar com o título: o Figma escreveu
                 "resgatados com sucesso!", que só fecha com título masculino
                 plural ("Baú de Coins resgatados" erra o número, "Rodada
                 Grátis resgatados" erra os dois). O título do prêmio é texto
                 livre do core, então a linha não pode depender dele. -->
            <span
              v-if="isClaimed"
              class="dcp-headline__ok"
              >{{ props.theme.descriptionText }}</span
            >
          </p>

          <!-- Arte: 160 na visão resumo, 100 quando a descrição entra. -->
          <div
            class="dcp-art"
            :class="{ 'dcp-art--won': celebrating }"
          >
            <!-- Leque de raios: acompanha o prêmio sempre que ele está
                 resgatado, inclusive ao reabrir. -->
            <span
              v-if="isClaimed"
              class="dcp-rays"
            />

            <!-- Faíscas: SÓ na passagem de resgatável pra resgatado. Elas
                 saem do centro, e uma coisa que sai do centro é um começo —
                 repetir isso num card que já estava verde lia como se algo
                 tivesse acontecido de novo. -->
            <template v-if="celebrating">
              <span
                v-for="(spark, i) in SPARKS"
                :key="i"
                class="dcp-spark"
                :style="{
                  '--a': `${spark.angle}deg`,
                  '--dist': `${spark.distance}px`,
                  '--d': `${spark.delay}ms`,
                  '--s': `${spark.size}px`,
                }"
              />
            </template>

            <!-- Escondido: interrogação no lugar da arte, na mesma caixa de
                 160, então o card não muda de altura com a opção ligada. -->
            <span
              v-if="hidePrize"
              aria-hidden="true"
              class="dcp-art__img dcp-art__img--hidden"
              >?</span
            >
            <img
              v-else
              alt=""
              class="dcp-art__img"
              :src="prizeImage"
            />
            <img
              v-if="isClaimed"
              alt=""
              class="dcp-art__check"
              :class="{ 'dcp-art__check--stamp': celebrating }"
              :src="asset('popup/check.svg')"
            />
          </div>

          <!-- Resgatar. Era uma arte do Figma (botao-resgatar.webp) e virou
               botão de verdade quando cor e legenda passaram a vir do
               backoffice: uma imagem não tem como trocar de cor nem de texto.
               O desenho segue o mesmo — 185x50, cantos arredondados e o mesmo
               contorno claro do botão USAR. -->
          <button
            v-if="isClaimable"
            :aria-label="props.theme.buttonLabel"
            class="dcp-claim"
            :disabled="claiming"
            type="button"
            @click="onClaim"
          >
            <span v-if="claiming">...</span>
            <span v-else>{{ props.theme.buttonLabel }}</span>
          </button>

          <!-- USAR no degrau resgatado (variante "Resgatado State 01" do
               Figma): leva o jogador ao destino cadastrado no prêmio. Sem
               `cta_url` na resposta o destino é a carteira; com o campo vazio
               o botão não existe — o core está dizendo que este prêmio não
               tem pra onde levar, e um botão que não leva a nada é pior que
               nenhum botão. -->
          <button
            v-else-if="hasUse"
            class="dcp-use"
            type="button"
            @click="onUse"
          >
            USAR
          </button>

          <!-- Recusa do resgate, com a mensagem que a store resolveu pelo
               código de erro do core. -->
          <p
            v-if="error"
            class="dcp-error"
            >{{ error }}</p
          >
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<!-- Sem scope: @keyframes contornam o reset global de transition. -->
<style>
/* Entrada só por opacidade, SEM escala.

   A escala (0.9 → 1) fazia o navegador rasterizar o card numa camada e
   redimensioná-la; ao terminar, a camada é descartada e tudo volta a ser
   desenhado direto na grade de pixels. Esse reencaixe é uma ticada de menos de
   um pixel, mas acontece no card INTEIRO de uma vez — por isso aparecia em
   toda abertura, e não só na imagem.

   Opacidade não mexe em geometria: o card é desenhado na mesma posição do
   primeiro ao último quadro, então não há nada pra reencaixar. */
.dcp-pop-enter-active {
  animation: dcp-pop 0.22s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: opacity;
}
.dcp-pop-leave-active {
  animation: dcp-pop 0.16s ease-in reverse forwards;
  will-change: opacity;
}
@keyframes dcp-pop {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

/* Ficha do prêmio: mesma entrada do popover da tela do calendário. */
.dcp-fade-enter-active {
  animation: dcp-fade 0.16s ease-out forwards;
}
.dcp-fade-leave-active {
  animation: dcp-fade 0.12s ease-in reverse forwards;
}
@keyframes dcp-fade {
  0% {
    transform: translateY(-6px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

<style scoped lang="scss">
// Acima do calendário (z-9996) — é um diálogo sobre ele.
.dcp-overlay {
  @apply fixed inset-0 z-[9997] flex items-center justify-center select-none;

  padding: 16px;

  img {
    -webkit-user-drag: none;
  }
}

.dcp-backdrop {
  @apply absolute inset-0;

  background: rgba(0, 0, 0, 0.6);
  animation: dcp-fade-in 0.2s ease forwards;
}

.dcp-backdrop--out {
  animation: dcp-fade-in 0.16s ease reverse forwards;
}

@keyframes dcp-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// 250px é a largura do Figma; encolhe só em telas muito estreitas.
.dcp-card {
  @apply relative z-10 flex flex-col items-center;

  // O TOM CORRENTE. Cada bloco de tom lá embaixo reaponta estes cinco, e tudo
  // que é derivado lê daqui — assim a derivação é escrita UMA vez, e não uma
  // por tom.
  --dcp-tone-from: var(--dcp-available-from);
  --dcp-tone-to: var(--dcp-available-to);
  --dcp-tone-edge: var(--dcp-available-edge);
  --dcp-tone-ink: var(--dcp-available-edge-ink);
  --dcp-tone-base-ink: var(--dcp-available-base-ink);
  --dcp-pop-ink: var(--dcp-available-top-ink);

  // Paleta do popover da ficha, DERIVADA do tom como a borda e o selo: o fundo
  // é o extremo escuro do próprio gradiente, a borda e o botão são a aresta.
  //
  // Ficou em hex fixo por tom até a revisão do ENG-857 — e aí o card ia pro
  // magenta e a ficha subia navy com título ciano e botão azul, o mesmo
  // destoar que motivou derivar a aresta.
  //
  // O destaque (o nome do prêmio na ficha) é a aresta puxada mais um passo na
  // direção da TINTA do popover: ele fica sobre o fundo escuro e precisa se
  // separar da borda. Puxar pra tinta e não pro branco é o que mantém isso
  // verdadeiro quando alguém configura um fundo claro — aí a tinta é preta e o
  // destaque escurece em vez de sumir.
  --dcp-pop-bg: var(--dcp-tone-from);
  --dcp-pop-border: var(--dcp-tone-edge);
  --dcp-pop-accent: color-mix(
    in srgb,
    var(--dcp-tone-edge) 55%,
    var(--dcp-pop-ink)
  );
  --dcp-pop-btn: var(--dcp-tone-edge);

  // A ARESTA de cada tom — borda do card e do selo — DERIVADA do fundo, não
  // configurada: é a base do gradiente clareada um passo.
  //
  // Derivar em vez de virar campo porque o desenho original já fazia isso. No
  // tom resgatado a borda e o selo eram exatamente a base (#009D12); no
  // disponível o selo era a cor do botão de então, um azul da mesma família da
  // base. Eram valores atrelados ao fundo, não escolhas próprias — e, fixos,
  // viravam ruído assim que alguém trocava o fundo por outra cor.
  //
  // Derivar MEXE no desenho de hoje, e isso é deliberado: com os valores de
  // fábrica a borda do resgatado sai de #009D12 pra ~#38B346, a do resgatável
  // de #1B54CF pra ~#4444BB, e o selo e o halo do resgatável de #1414DD pro
  // #0F0FA8 da base. Reproduzir os antigos exigiria guardar três cores que não
  // saem de nenhum campo — e seriam justamente as que não acompanhariam o fundo
  // configurado, que é o problema que esta derivação existe pra resolver.
  //
  // `color-mix` e não uma conta em JS: a prévia do backoffice usa a MESMA
  // expressão, então o que o operador vê e o que o jogador vê não podem
  // divergir por arredondamento.
  --dcp-available-edge: color-mix(in srgb, var(--dcp-available-to) 78%, white);
  --dcp-claimed-edge: color-mix(in srgb, var(--dcp-claimed-to) 78%, white);

  width: 250px;
  max-width: 100%;
  // Todos os tons na mesma altura, medida no tom RESGATADO, que é o mais alto
  // dos comuns desde que o USAR entrou: 1px de borda + selo 40 + manchete 54 +
  // arte 160 + botão 45 + 3 gaps de 10 + 16 de padding + 1px de borda = 347.

  // A manchete são TRÊS linhas, não duas: o título ocupa uma e a frase verde
  // ("Resgate concluído com sucesso!") não cabe em 218px de largura útil na
  // Plus Jakarta Sans 15/800, então quebra. É por isso que o número não é o 327
  // do frame do Figma — lá a manchete inteira cabia em duas linhas porque era
  // uma frase só, e a nossa é deliberadamente duas (ver o comentário da
  // manchete no template).

  // O resgatável mede 316 por conta própria e a folga vai pras margens
  // automáticas da arte. Sem isso o card CRESCERIA 20px na virada
  // resgatável → resgatado, e mudar de tamanho durante a virada de cor endurece
  // a transição. É `min-height` porque o tom com mensagem de erro é mais alto e
  // continua podendo crescer — e um título que quebre em duas linhas também.
  min-height: 347px;
  // Sem padding no topo: o selo "Nº DIA" encosta na borda de cima.
  padding: 0 16px 16px;
  gap: 10px;
  border: 1px solid;
  border-radius: 16px;
}

/* ---------- selo Nº DIA ---------- */
.dcp-badge {
  @apply flex items-center justify-center shrink-0 text-center;

  // O fundo do selo é a BASE do gradiente, que é campo livre no backoffice —
  // então o "Nº Dia" escolhe a tinta em vez de ser branco fixo.
  color: var(--dcp-tone-base-ink);

  // Mínimo do Figma; número de dois dígitos + "º Dia" em 24/800 chega perto dos
  // 102px, então o selo pode crescer em vez de clipar.
  min-width: 102px;
  height: 40px;
  padding-inline: 8.125px;
  border: 1px solid;
  // Só os cantos de baixo — o selo pende da borda superior do card.
  border-radius: 0 0 20px 20px;
  font-size: 24px;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
}

/* ---------- textos ---------- */
.dcp-headline {
  @apply w-full shrink-0 text-white text-center;

  font-size: 15px;
  font-weight: 800;
  line-height: 1.2;
  // Os dois textos daqui vêm do backoffice agora (o nome do prêmio e a frase de
  // confirmação), então nenhum é mais garantido curto. `w-full` porque um
  // parágrafo num flex coluna centralizado cresce até o conteúdo e VAZA do card
  // em vez de quebrar; `anywhere` porque um texto sem espaços vazaria mesmo com
  // a largura fixa.
  overflow-wrap: anywhere;
}

.dcp-headline__ok {
  display: block;
  color: var(--dcp-desc);
}

/* ---------- ficha do prêmio (popover do (i)) ---------- */
// Mesmo desenho do popover da tela do calendário (.dc-info): caixa navy com
// barriguinha apontando pro botão e scrim de clique-fora.
.dcp-info-scrim {
  @apply absolute inset-0;

  z-index: 8;
}

.dcp-info {
  position: absolute;
  z-index: 9;
  // 9 (topo do (i)) + 30 (altura) + 8 de folga.
  top: 47px;
  left: 10px;
  width: 200px;
  padding: 10px;
  border: 1px solid var(--dcp-pop-border);
  border-radius: 10px;
  background: var(--dcp-pop-bg);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
  text-align: left;

  // Centrada sob o (i): o botão vai de 10 a 40, centro em 25; o popover começa
  // em 10, então o deslocamento é 15 menos metade do quadrado.
  &::before {
    content: '';

    position: absolute;
    top: -6px;
    left: 9px;
    width: 12px;
    height: 12px;
    border-top: 1px solid var(--dcp-pop-border);
    border-left: 1px solid var(--dcp-pop-border);
    border-radius: 3px 0 0 0;
    background: var(--dcp-pop-bg);
    transform: rotate(45deg);
  }
}

// Nome do prêmio no ciano do Figma, que antes era o título da visão de
// detalhe do card.
.dcp-info__name {
  margin-bottom: 4px;
  color: var(--dcp-pop-accent);
  font-size: 12px;
  font-weight: 800;
  line-height: 1.2;
}

.dcp-info__text {
  // A tinta legível sobre o fundo do popover, a 85% como era o branco.
  color: color-mix(in srgb, var(--dcp-pop-ink) 85%, transparent);
  font-size: 11px;
  line-height: 1.35;
}

.dcp-info__ok {
  @apply w-full font-bold;

  margin-top: 8px;
  padding: 6px;
  border-radius: 7px;
  background: var(--dcp-pop-btn);
  // O botão é a aresta, então a legenda usa a tinta da aresta.
  color: var(--dcp-tone-ink);
  font-size: 11px;
}

/* ---------- arte ---------- */
// A caixa é 160x160 FIXO em todos os tons, e a folga da altura mínima vai nas
// margens automáticas: o selo continua pendurado na borda de cima, o botão
// continua no rodapé e o que sobra se reparte em volta da arte.

// A caixa NÃO pode esticar. As artes vêm do core em proporções diferentes (o
// baú é 224x288, retrato), e com `object-fit: contain` numa caixa mais alta o
// mesmo prêmio renderiza maior — media 160x206 no card sem botão contra
// 125x161 no card com botão. Tamanho de prêmio não pode depender do tom.
.dcp-art {
  @apply relative flex shrink-0 items-center justify-center;

  width: 160px;
  height: 160px;
  margin-block: auto;
}

// `relative` + z-index só pra fixar a ordem contra as camadas da comemoração:
// os raios (z-0) ficam atrás da arte e as faíscas (z-2) na frente.
.dcp-art__img {
  @apply relative w-full h-full;

  z-index: 1;
  object-fit: contain;
}

// Mesma caixa da arte, então o card mantém a altura com a opção ligada.
.dcp-art__img--hidden {
  @apply flex items-center justify-center;

  color: rgba(255, 255, 255, 0.4);
  font-size: 96px;
  font-weight: 800;
  line-height: 1;
}

// Check do estado resgatado, centrado sobre a arte.
.dcp-art__check {
  @apply absolute;

  z-index: 3;
  left: 50%;
  top: 50%;
  width: 61px;
  height: 52px;
  transform: translate(-50%, -50%);
}

/* ---------- botões ---------- */
// RESGATAR: 185x50, a mesma caixa da arte que ele substituiu. A cor vem do
// backoffice (`--dcp-claim-btn`) e a legenda é texto, então os dois mudam sem
// passar por um novo asset.
//
// O contorno branco a 50% é o mesmo do USAR logo abaixo: os dois botões do
// popup são a mesma peça em tons diferentes, e a borda é o que os liga.
.dcp-claim {
  @apply relative shrink-0 flex items-center justify-center font-extrabold uppercase;

  width: 185px;
  height: 50px;
  padding-inline: 12px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 10px;
  background: var(--dcp-claim-btn);
  // A cor do botão é livre no backoffice, então a legenda escolhe a tinta em
  // vez de ser branca fixa (ver `readableOn`): num botão amarelo o branco some.
  color: var(--dcp-claim-ink);
  font-size: 20px;
  letter-spacing: 0.4px;
  // Legenda longa demais encurta em vez de estourar a caixa: o texto é livre no
  // backoffice e o card tem largura fixa.
  overflow: hidden;

  span {
    @apply block truncate max-w-full;
  }

  &:disabled {
    @apply opacity-70;
  }

  &:not(:disabled):active {
    filter: brightness(0.94);
  }
}

// USAR: pílula vazada da variante resgatada do Figma (instância "Botão Ver
// Mais", 206x45). O fundo é transparente de propósito — o gradiente verde do
// card é que aparece ali dentro, conferido pixel a pixel no arquivo — e a borda
// é branco a 50%, que é o mesmo branco do texto com a opacidade do Figma.
.dcp-use {
  @apply flex shrink-0 items-center justify-center text-white font-extrabold uppercase;

  width: 206px;
  max-width: 100%;
  height: 45px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  // Pílula: no Figma o raio é metade da altura, então vale arredondar sem conta.
  border-radius: 999px;
  font-size: 14px;
  letter-spacing: 0.4px;

  // Sem `transition` (o reset global mata): o toque acende na hora e apaga na
  // hora, que é o que se espera de um botão de contorno.
  &:active {
    background: rgba(255, 255, 255, 0.12);
  }
}

.dcp-error {
  @apply w-full shrink-0 text-center;

  color: #ffb4a1;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.35;
}

/* ---------- ícones ---------- */
.dcp-icon {
  @apply absolute z-10;

  top: 9px;
  width: 30px;
  height: 30px;
  // `color` porque o círculo dos SVG inline é `currentColor`: a aresta do tom
  // corrente, a mesma da borda e do selo — os ícones ficam no topo do card,
  // onde o gradiente é escuro, e a aresta é a ponta clara da paleta: destaca
  // sem trazer uma cor de fora dela.
  color: var(--dcp-tone-edge);

  svg {
    @apply w-full h-full;
  }

  // O GLIFO por cima do círculo, sobrescrevendo o `fill="#fff"` do path. Branco
  // fixo desaparecia assim que alguém configurasse um fundo claro — e um dos
  // dois é o X de fechar, que é como se sai daqui.
  path {
    fill: var(--dcp-tone-ink);
  }
}

.dcp-icon--info {
  left: 10px;
}

.dcp-icon--close {
  right: 12px;
}

/* ---------- tons ---------- */
// Ângulos de 180.34° no Figma (arredondamento do editor) = 180deg.
//
// O que cada tom escreve aqui é só o que é PRÓPRIO dele: o gradiente, o halo e,
// no resgatado, a virada do `--dcp-tone-*`. Tudo que sai desses valores — a
// aresta, o glifo dos ícones, a paleta da ficha — está declarado uma vez só lá
// em cima, no `.dcp-card`.
// Azul do Figma. Chegou a ir pro laranja da célula, pra a cor dizer a mesma
// coisa nos dois lados, e o Lucas preferiu o azul de volta aqui.
.dcp-card--claimable {
  border-color: var(--dcp-available-edge);
  background: linear-gradient(
    180deg,
    var(--dcp-available-from) 32.836%,
    var(--dcp-available-to) 99.762%
  );
  filter: drop-shadow(0 0 10px var(--dcp-available-to));

  .dcp-badge {
    border-color: var(--dcp-available-edge);
    background: var(--dcp-available-to);
  }
}

// Mesmo azul do resgatável (decisão do Lucas): o degrau que ainda vem não é
// um estado de erro nem de espera, é só a vez que não chegou — então o card é
// o mesmo. O que separa os dois é o HALO e o botão: só o resgatável brilha e
// só ele tem RESGATAR.
.dcp-card--locked {
  border-color: var(--dcp-available-edge);
  background: linear-gradient(
    180deg,
    var(--dcp-available-from) 32.836%,
    var(--dcp-available-to) 99.762%
  );

  .dcp-badge {
    border-color: var(--dcp-available-edge);
    background: var(--dcp-available-to);
  }
}

.dcp-card--claimed {
  // Vira o tom resgatado, e com ele TUDO que é derivado: a aresta da borda e do
  // selo, a cor e o glifo dos ícones e a paleta inteira do popover da ficha.
  --dcp-tone-from: var(--dcp-claimed-from);
  --dcp-tone-to: var(--dcp-claimed-to);
  --dcp-tone-edge: var(--dcp-claimed-edge);
  --dcp-tone-ink: var(--dcp-claimed-edge-ink);
  --dcp-tone-base-ink: var(--dcp-claimed-base-ink);
  --dcp-pop-ink: var(--dcp-claimed-top-ink);

  border-color: var(--dcp-claimed-edge);
  // `to top`: a primeira parada é a de BAIXO, por isso o `--to` vem antes.
  background: linear-gradient(
    to top,
    var(--dcp-claimed-to) 77.027%,
    var(--dcp-claimed-from) 87.838%
  );

  .dcp-badge {
    border-color: var(--dcp-claimed-edge);
    background: var(--dcp-claimed-to);
  }
}

/* ---------- comemoração do resgate ---------- */

// A troca de tom é instantânea no DOM (a classe pula de --claimable pra
// --claimed) e o reset global de transition impede o caminho normal. Então a
// animação FAZ o papel da transição: o quadro 0% repinta o card de azul e ele
// desliza até o verde, que é exatamente o valor estático do tom resgatado — no
// fim da animação nada muda de lugar, o CSS assume sem emenda.

// Junto vai o pulinho, numa animação separada: o pulo (transform) precisa da
// curva adiantada pra ter impacto e a virada de cor precisa do contrário — na
// mesma curva do pulo, a cor já chegava no verde aos 120ms.

// Cor sólida (borda, selo) o navegador interpola. GRADIENTE não: o Chromium
// troca `background-image` de forma DISCRETA, no meio da animação — era isso
// que dava o corte seco de azul pra verde enquanto o selo atravessava liso.
// Por isso o gradiente antigo vira uma CAMADA por cima do novo, que se apaga:
// o cross-fade passa a ser de opacidade, que interpola. O `z-index: -1` mantém
// a camada entre o fundo do card e o conteúdo, senão ela cobriria o selo e a
// manchete.

// NADA na comemoração mexe no CONTORNO do card, e isso é regra, não acaso.
// Já saíram daqui, um por um: o `filter: drop-shadow` (que ainda por cima
// re-rasterizava a subárvore inteira a cada quadro), o `scale` do card (1px de
// borda escalado é 1px mudando de espessura na frente do olho) e o halo que
// abria pra fora. Todos liam como a borda se mexendo, e borda se mexendo fica
// deselegante. O que celebra é o miolo: a cor atravessando, o solavanco da
// arte, o carimbo do check e as faíscas.

// `isolation: isolate` é obrigatório aqui: era o `filter` que criava o contexto
// de empilhamento, e sem contexto o `z-index: -1` do ::before cai atrás do
// fundo do card — o cross-fade simplesmente sumiria.
.dcp-card--won {
  isolation: isolate;
  // A borda vai em `ease-in-out` e um pouco mais longa que o fundo: ela é uma
  // linha de 1px, então qualquer trecho rápido da curva vira um corte visível.
  // Ficar um respiro atrás do fundo lê como suavidade, não como atraso.
  animation: dcp-won-tone 780ms ease-in-out;

  // Camada do gradiente antigo, que se apaga.
  &::before {
    content: '';

    position: absolute;
    z-index: -1;
    inset: 0;
    // 15 e não `inherit`: o card tem raio 16 e borda 1, e a caixa de
    // preenchimento (onde o `inset: 0` ancora) tem o raio interno. Com 16 a
    // camada azul escapava 1px na curva das quinas.
    border-radius: 15px;
    // A camada que some é o tom DISPONÍVEL: o cross-fade tem que sair da mesma
    // cor que o card acabou de ter, senão a virada pisca no azul de fábrica.
    background: linear-gradient(
      180deg,
      var(--dcp-available-from) 32.836%,
      var(--dcp-available-to) 99.762%
    );
    will-change: opacity;
    animation: dcp-won-fade 600ms ease-out forwards;
  }
}

@keyframes dcp-won-fade {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

// Só a borda — o fundo é o cross-fade acima. O `filter` NÃO entra aqui: além
// de caro, keyframe vence declaração normal, então ele anulava o `filter: none`
// do card e voltava de estalo quando a classe saía.
//
// As paradas são as MESMAS variáveis do estático (keyframe resolve `var()` no
// escopo do elemento). Estiveram em hex de fábrica e isso quebrava a promessa
// do parágrafo acima nos dois sentidos: mesmo sem tema o quadro 100% terminava
// no `#009d12` enquanto o card estático já usava a aresta, então o contorno
// PULAVA quando a classe saía; e com tema configurado a virada piscava o azul e
// o verde de fábrica antes de assumir a cor do operador.
@keyframes dcp-won-tone {
  0% {
    border-color: var(--dcp-available-edge);
  }

  100% {
    border-color: var(--dcp-claimed-edge);
  }
}

// O selo atravessa junto, senão ele salta pro verde sozinho enquanto o resto
// do card ainda está no meio do caminho.
.dcp-card--won .dcp-badge {
  animation: dcp-won-badge 780ms ease-in-out;
}

@keyframes dcp-won-badge {
  0% {
    border-color: var(--dcp-available-edge);
    background: var(--dcp-available-to);
  }

  100% {
    border-color: var(--dcp-claimed-edge);
    background: var(--dcp-claimed-to);
  }
}

// Leque de raios atrás do prêmio. A máscara radial apaga o centro (pra não
// cobrir a arte) e as pontas, então o leque não termina num círculo duro.
.dcp-rays {
  position: absolute;
  z-index: 0;
  left: 50%;
  top: 50%;
  width: 300px;
  height: 300px;
  background: repeating-conic-gradient(
    rgba(255, 255, 255, 0.62) 0deg 5deg,
    rgba(255, 255, 255, 0) 5deg 21deg
  );
  mask-image: radial-gradient(
    closest-side,
    transparent 14%,
    #000 34%,
    transparent 74%
  );
  -webkit-mask-image: radial-gradient(
    closest-side,
    transparent 14%,
    #000 34%,
    transparent 74%
  );
  pointer-events: none;
  will-change: transform, opacity;
  // Duas animações, cada uma na SUA propriedade, pra não se sobrescreverem: a
  // entrada cuida da opacidade e o giro cuida do transform, pra sempre. Uma só
  // de cada, IGUAL nos dois momentos (resgate e reabertura) — animação que
  // depende de estado troca quando o estado troca, e a troca sempre aparece
  // como piscada.
  animation:
    dcp-rays-in 500ms ease-out both,
    dcp-rays-spin 34s linear infinite;
}

// Sobe direto até o brilho de fundo, sem passar por um pico. O pico era o
// clarão do estouro, mas como a animação é a mesma na reabertura, ali ele
// virava um lampejo em cima da arte e do botão USAR — o leque tem 300px e
// cobre os dois.
@keyframes dcp-rays-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 0.4;
  }
}

@keyframes dcp-rays-spin {
  0% {
    transform: translate(-50%, -50%) scale(0.82) rotate(0deg);
  }

  100% {
    transform: translate(-50%, -50%) scale(0.82) rotate(360deg);
  }
}

// Faíscas: `rotate` e DEPOIS `translateX` é intencional aqui — cada faísca sai
// pelo eixo já girado, que é o que produz o estouro radial.
.dcp-spark {
  position: absolute;
  z-index: 2;
  left: 50%;
  top: 50%;
  width: var(--s);
  height: var(--s);
  border-radius: 50%;
  background: radial-gradient(circle, #fff 38%, #d6ffdd 62%, transparent 72%);
  // O halo é o que faz a faísca ler sobre o verde do card.
  box-shadow:
    0 0 8px rgba(255, 255, 255, 0.95),
    0 0 18px rgba(120, 255, 150, 0.75);
  pointer-events: none;
  will-change: transform, opacity;
  animation: dcp-spark 820ms cubic-bezier(0.15, 0.7, 0.3, 1) var(--d) both;
}

// Uma passagem só, sem looping: sai do centro, chega na ponta e apaga.
@keyframes dcp-spark {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) rotate(var(--a)) translateX(0) scale(0.4);
  }

  18% {
    opacity: 1;
  }

  100% {
    opacity: 0;
    transform: translate(-50%, -50%) rotate(var(--a)) translateX(var(--dist))
      scale(0.55);
  }
}

// A arte recebe o prêmio com um solavanco.
.dcp-art--won .dcp-art__img {
  animation: dcp-won-art 620ms cubic-bezier(0.2, 0.8, 0.3, 1);
}

@keyframes dcp-won-art {
  0% {
    transform: scale(0.8);
  }

  50% {
    transform: scale(1.12);
  }

  100% {
    transform: scale(1);
  }
}

// Check batendo como carimbo, com um giro curto pra assentar.
.dcp-art__check--stamp {
  animation: dcp-stamp 560ms cubic-bezier(0.2, 0.9, 0.3, 1) 120ms both;
}

@keyframes dcp-stamp {
  0% {
    opacity: 0;
    transform: translate(-50%, -50%) scale(2.6) rotate(-16deg);
  }

  55% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(0.92) rotate(4deg);
  }

  100% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1) rotate(0deg);
  }
}

// A linha verde da manchete é o único conteúdo que nasce com o resgate. Entra
// por cima da virada de cor, não junto com o pulo: mais lenta, com
// deslocamento curto, pra ler como coisa que aparece e não como coisa que
// salta.
.dcp-headline--won {
  animation: dcp-rise 560ms ease-out 300ms both;
}

@keyframes dcp-rise {
  0% {
    opacity: 0;
    transform: translateY(4px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
